import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  CircularProgress
} from '@mui/material';
import { Save, Cancel } from '@mui/icons-material';

function MedicalHistoryDialog({ open, onClose, visitId, existingHistory, onSuccess, onError }) {
  const [medicalHistory, setMedicalHistory] = useState({
    chiefComplaint: '',
    pastMedicalHistory: '',
    currentMedications: '',
    allergies: '',
    familyHistory: '',
    lifestyleHabits: ''
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (existingHistory) {
      setMedicalHistory({
        chiefComplaint: existingHistory.chief_complaint || '',
        pastMedicalHistory: existingHistory.past_medical_history || '',
        currentMedications: existingHistory.current_medications || '',
        allergies: existingHistory.allergies || '',
        familyHistory: existingHistory.family_history || '',
        lifestyleHabits: existingHistory.lifestyle_habits || ''
      });
    }
  }, [existingHistory]);

  const handleSubmit = async () => {
    setSaving(true);
    try {
      await onSuccess(medicalHistory);
      handleClose();
    } catch (error) {
      if (onError) {
        onError(error.response?.data?.message || 'Failed to save medical history');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    if (!saving) {
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {existingHistory ? 'Update Medical History' : 'Add Medical History'}
      </DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              rows={3}
              label="Chief Complaint"
              value={medicalHistory.chiefComplaint}
              onChange={(e) => setMedicalHistory({ ...medicalHistory, chiefComplaint: e.target.value })}
              placeholder="Patient's main complaint or reason for visit"
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              rows={3}
              label="Past Medical History"
              value={medicalHistory.pastMedicalHistory}
              onChange={(e) => setMedicalHistory({ ...medicalHistory, pastMedicalHistory: e.target.value })}
              placeholder="Previous illnesses, surgeries, hospitalizations"
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              rows={2}
              label="Current Medications"
              value={medicalHistory.currentMedications}
              onChange={(e) => setMedicalHistory({ ...medicalHistory, currentMedications: e.target.value })}
              placeholder="List of current medications"
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              rows={2}
              label="Allergies"
              value={medicalHistory.allergies}
              onChange={(e) => setMedicalHistory({ ...medicalHistory, allergies: e.target.value })}
              placeholder="Known allergies (food, drugs, environmental)"
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              rows={2}
              label="Family History"
              value={medicalHistory.familyHistory}
              onChange={(e) => setMedicalHistory({ ...medicalHistory, familyHistory: e.target.value })}
              placeholder="Family medical history (hereditary conditions)"
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              rows={2}
              label="Lifestyle Habits"
              value={medicalHistory.lifestyleHabits}
              onChange={(e) => setMedicalHistory({ ...medicalHistory, lifestyleHabits: e.target.value })}
              placeholder="Smoking, alcohol, exercise, diet, etc."
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} startIcon={<Cancel />} disabled={saving}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="primary"
          startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <Save />}
          disabled={saving}
        >
          {saving ? 'Saving...' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default MedicalHistoryDialog;